import { inject, Injectable } from '@angular/core';
import {
  CurrencyInterface,
  FilterGroupOperator,
  FilterOperator,
  GetOpportunityValueSummaryResponseInterface,
} from '@vendasta/crm';
import { BehaviorSubject, combineLatest, of, ReplaySubject, shareReplay } from 'rxjs';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { CrmInjectionToken, ObjectType } from '../../tokens-and-interfaces';
import { filter, map, switchMap } from 'rxjs/operators';
import { OpportunityService, StandardIds } from '../../shared-services';
import { convertGalaxyFilters } from '../../utils';

export interface StageAmount {
  stageId?: string;
  amounts?: Amounts;
}

export interface ValueSummary {
  pipeline?: Amounts;
  stages?: StageAmount[];
}

export interface Amounts {
  total?: CurrencyInterface;
  estimated?: CurrencyInterface;
}

@Injectable()
export class ValueSummaryService {
  protected readonly opportunityService = inject(OpportunityService);
  private readonly config = inject(CrmInjectionToken);

  private objectType$$ = new ReplaySubject<ObjectType>();
  private pipelineID$$ = new ReplaySubject<string>();
  private filters$$ = new BehaviorSubject<GalaxyFilterInterface[]>([]);

  setPipelineId(pipelineId: string) {
    this.pipelineID$$.next(pipelineId);
  }

  setObjectType(objectType: ObjectType): void {
    this.objectType$$.next(objectType);
  }

  setFilters(filters: GalaxyFilterInterface[]): void {
    this.filters$$.next(filters);
  }

  valueSummary$ = combineLatest([this.config.namespace$, this.objectType$$, this.pipelineID$$, this.filters$$]).pipe(
    filter(([namespace, objectType, pipelineId]) => !!namespace && !!objectType && !!pipelineId),
    switchMap(([namespace, objectType, pipelineId, filters]) => {
      switch (objectType) {
        case 'Opportunity':
          return this.opportunityService.getValueSummary({
            namespace,
            filters: {
              operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
              filters: [
                {
                  fieldId: StandardIds.OpportunityPipelineID,
                  operator: FilterOperator.FILTER_OPERATOR_IS,
                  values: [{ string: pipelineId }],
                },
                ...(convertGalaxyFilters(filters)?.filters ?? []),
              ],
            },
          });
        default:
          return of(null);
      }
    }),
    map((response) => this.mapValueSummary(response)),
    shareReplay(1),
  );

  private mapValueSummary(response: GetOpportunityValueSummaryResponseInterface | null): ValueSummary {
    if (!response) {
      return {};
    }

    return {
      pipeline: {
        total: response?.pipeline?.total,
        estimated: response?.pipeline?.estimated,
      },
      stages: response?.stages?.map((stage) => ({
        stageId: stage?.stageId,
        amounts: {
          total: stage?.amounts?.total,
          estimated: stage?.amounts?.estimated,
        },
      })),
    };
  }
}
